import type { PricingConfig } from '../types/pricing.js'

export const DEFAULT_PRICING: PricingConfig = {
  version: '1.0',
  lastUpdated: '2024-12-08',

  // ==========================================================================
  // MATERIAL COSTS (Actual supplier prices - Hood, Wurth, Builder FirstSource)
  // ==========================================================================
  materials: {
    // Plywood per 4'×8' sheet (32 sqft)
    plywood: {
      '11/16"': 40,   // Structural - Birch
      '1/2"': 30,     // Panels
      '1/4"': 20      // Concealment
    },

    // Lumber per board foot
    lumber: {
      poplar: 2.00,
      hardMaple: 3.33,
      whiteOak: 4.67,
      walnut: 10.00
    },

    // Stair tread lumber (sold per piece, not board foot)
    stairTreads: {
      // 1-1/16" x 48" x 11-1/4" Pine - Lowes
      '1-1/16_pine_48': {
        price: 15.00,
        dimensions: '1-1/16" x 48" x 11-1/4"',
        supplier: 'Lowes',
        notes: 'Standard pine stair tread'
      }
    },

    // Edge banding per linear foot
    edgeBanding: {
      birch: 0.50,
      maple: 0.50,
      whiteOak: 0.55,
      walnut: 0.60
    },

    // Hardware (per unit or per box as noted)
    hardware: {
      screws_1_25_100: 10,      // 1-1/4" Wood Screws per 100
      screws_2_5_100: 12,       // 2-1/2" Wood Screws per 100
      screws_pocket_250: 18,    // Pocket Screws per 250
      screws_4_50: 15,          // 4" Wood Screws per 50
      nails_16ga_1000: 12,      // 16ga Finish Nails per 1000
      staples_narrow_1000: 10,  // Narrow Crown Staples per 1000
      glue_32oz: 8,             // Wood Glue 32oz bottle
      brackets_8: 12            // Support Brackets pack of 8
    },

    // Finish materials per gallon
    finish: {
      primer: { cost: 25, coverage: 350 },        // sqft coverage
      paintWhite: { cost: 35, coverage: 400 },
      paintBlack: { cost: 35, coverage: 400 },
      paintCustom: { cost: 45, coverage: 400 },
      stain: { cost: 30, coverage: 300 },
      polyurethane: { cost: 40, coverage: 500 }
    }
  },

  // ==========================================================================
  // LABOR
  // ==========================================================================
  laborRate: 35,  // per hour

  // ==========================================================================
  // FABRICATION LABOR RATES (Partner Payouts)
  // These rates determine how much we pay Millworks partners per operation
  // ==========================================================================
  fabLaborRates: {
    ripCut: 10,           // Per rip cut (plywood)
    crossCut: 5,          // Per cross cut (lumber)
    edgeBanding: 1,       // Per linear foot
    countersunkHole: 0.15, // Per hole
    pocketHole: 0.25,     // Per pocket hole
    baseAssembly: 100,    // Base assembly fee per bed unit
  },

  // ==========================================================================
  // MARKUP
  // ==========================================================================
  markup: 3.5,    // Internal cost × 3.5 = Client price

  // ==========================================================================
  // BASE BED CONFIGURATIONS
  // Internal costs include: materials + labor + waste/overhead (15%)
  // ==========================================================================
  configs: {
    // BUNK CONFIGURATIONS
    'Twin/Twin': {
      internalCost: 1341,
      laborHours: 23.5,
      materialsCost: 519,
      laborCost: 823,
      clientPrice: 4695,
      fullPrice: 6090
    },
    'Twin/Full': {
      internalCost: 1456,
      laborHours: 25.5,
      materialsCost: 558,
      laborCost: 898,
      clientPrice: 5096,
      fullPrice: 6491
    },
    'Twin/Queen': {
      internalCost: 1577,
      laborHours: 28.0,
      materialsCost: 597,
      laborCost: 980,
      clientPrice: 5520,
      fullPrice: 6915
    },
    'Twin XL/Twin XL': {
      internalCost: 1398,
      laborHours: 24.5,
      materialsCost: 540,
      laborCost: 858,
      clientPrice: 4893,
      fullPrice: 6288
    },
    'Twin XL/Full': {
      internalCost: 1513,
      laborHours: 26.5,
      materialsCost: 580,
      laborCost: 933,
      clientPrice: 5296,
      fullPrice: 6691
    },
    'Twin XL/Queen': {
      internalCost: 1740,
      laborHours: 30.0,
      materialsCost: 620,
      laborCost: 1050,
      clientPrice: 6090,
      fullPrice: 7485
    },
    'Full/Full': {
      internalCost: 1577,
      laborHours: 28.0,
      materialsCost: 597,
      laborCost: 980,
      clientPrice: 5520,
      fullPrice: 6915
    },
    'Full/Queen': {
      internalCost: 1740,
      laborHours: 31.0,
      materialsCost: 638,
      laborCost: 1085,
      clientPrice: 6090,
      fullPrice: 7485
    },
    'Queen/Queen': {
      internalCost: 1903,
      laborHours: 35.0,
      materialsCost: 678,
      laborCost: 1225,
      clientPrice: 6660,
      fullPrice: 8056
    },
    // LOFT CONFIGURATIONS (Single elevated bed - ~70% of bunk price)
    'Twin Loft': {
      internalCost: 939,
      laborHours: 16.5,
      materialsCost: 363,
      laborCost: 576,
      clientPrice: 3287,
      fullPrice: 4682
    },
    'Twin XL Loft': {
      internalCost: 979,
      laborHours: 17.0,
      materialsCost: 378,
      laborCost: 601,
      clientPrice: 3425,
      fullPrice: 4820
    },
    'Full Loft': {
      internalCost: 1104,
      laborHours: 19.5,
      materialsCost: 418,
      laborCost: 686,
      clientPrice: 3864,
      fullPrice: 5259
    },
    'Queen Loft': {
      internalCost: 1332,
      laborHours: 24.5,
      materialsCost: 475,
      laborCost: 858,
      clientPrice: 4662,
      fullPrice: 6057
    },
    // LEGACY CONFIGURATIONS (keep for backward compatibility)
    'Triple': {
      internalCost: 1910,
      laborHours: 35.0,
      materialsCost: 685,
      laborCost: 1225,
      clientPrice: 6685,
      fullPrice: 8080
    },
    'Quad': {
      internalCost: 2575,
      laborHours: 47.5,
      materialsCost: 912,
      laborCost: 1663,
      clientPrice: 9013,
      fullPrice: 10408
    },
    'L-Shaped': {
      internalCost: 2037,
      laborHours: 37.5,
      materialsCost: 724,
      laborCost: 1313,
      clientPrice: 7130,
      fullPrice: 8525
    }
  },

  // ==========================================================================
  // WOOD UPGRADES (Client price addition)
  // ==========================================================================
  woodUpgrades: {
    // BUNK CONFIGURATIONS
    'Twin/Twin': { maple: 450, whiteOak: 750, walnut: 1500 },
    'Twin/Full': { maple: 475, whiteOak: 800, walnut: 1575 },
    'Twin/Queen': { maple: 525, whiteOak: 875, walnut: 1725 },
    'Twin XL/Twin XL': { maple: 465, whiteOak: 775, walnut: 1550 },
    'Twin XL/Full': { maple: 490, whiteOak: 825, walnut: 1625 },
    'Twin XL/Queen': { maple: 650, whiteOak: 1100, walnut: 2200 },
    'Full/Full': { maple: 525, whiteOak: 875, walnut: 1725 },
    'Full/Queen': { maple: 650, whiteOak: 1100, walnut: 2200 },
    'Queen/Queen': { maple: 800, whiteOak: 1325, walnut: 2650 },
    // LOFT CONFIGURATIONS (~70% of bunk upgrades)
    'Twin Loft': { maple: 315, whiteOak: 525, walnut: 1050 },
    'Twin XL Loft': { maple: 325, whiteOak: 540, walnut: 1085 },
    'Full Loft': { maple: 365, whiteOak: 610, walnut: 1210 },
    'Queen Loft': { maple: 560, whiteOak: 925, walnut: 1855 },
    // LEGACY CONFIGURATIONS
    'Triple': { maple: 675, whiteOak: 1125, walnut: 2250 },
    'Quad': { maple: 900, whiteOak: 1500, walnut: 3000 },
    'L-Shaped': { maple: 725, whiteOak: 1200, walnut: 2400 }
  },

  // ==========================================================================
  // ADD-ONS (with detailed cost breakdown for PricingEditor)
  // ==========================================================================
  addOns: {
    staircase_3_drawers: {
      label: 'Staircase without Drawers',
      internalCost: 529,
      clientPrice: 1895,
      laborHours: 8
    },
    staircase_blum: {
      label: 'Staircase with Soft-Close Drawers',
      internalCost: 664,
      clientPrice: 2370,
      laborHours: 8
    },
    trundle: {
      label: 'Trundle Bed',
      internalCost: 237,
      clientPrice: 850,
      laborHours: 4
    },
    slide: {
      label: 'Slide',
      internalCost: 461,
      clientPrice: 1650,
      laborHours: 2.5
    },
    desk: {
      label: 'Built-in Desk',
      internalCost: 305,
      clientPrice: 1095,
      laborHours: 5
    },
    led_lighting: {
      label: 'LED Lighting Package',
      internalCost: 158,
      clientPrice: 565,
      laborHours: 2
    },
    storage_drawers_2: {
      label: 'Under-bed Storage (2 drawers)',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    storage: {
      label: 'Under-bed Storage Drawers',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    storage_drawers: {
      label: 'Under-bed Storage Drawers',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    upholstery: {
      label: 'Custom Upholstered Panels',
      internalCost: 800,
      clientPrice: 2000,
      laborHours: 6
    },
    carcass: {
      label: 'Custom Carcass',
      internalCost: 140,
      clientPrice: 350,
      laborHours: 3
    }
  },

  // ==========================================================================
  // DELIVERY & INSTALLATION
  // ==========================================================================
  delivery: {
    local: 0,           // Walton County, FL
    regional: 500,      // FL, GA, AL, MS, LA (includes SC)
    national: 650       // Continental US
  },

  // ==========================================================================
  // SHIPPING & PACKAGING COSTS (Internal - for Job Costing)
  // ==========================================================================
  shippingCosts: {
    // Base LTL freight rates from Florida
    ltlBase: {
      local: 0,           // Client pickup / local delivery
      regional: 300,      // FL, GA, AL, MS, LA, SC (~300 miles)
      midRange: 450,      // TN, NC, AR (~500 miles)
      national: 650       // Continental US (800+ miles)
    },
    // Packaging materials per bed unit
    packaging: {
      cardboard: 25,      // Cardboard boxes/corners
      bubbleWrap: 15,     // Bubble wrap
      shrinkWrap: 10,     // Plastic shrink wrap
      pallets: 35,        // Pallet cost
      strapping: 5,       // Strapping/banding
      labels: 2,          // Shipping labels
      instructionManual: 8 // Printed instruction manual per bed
    },
    // Per-bed packaging total = $100
    packagingPerBed: 100,
    // Markup on shipping (to cover handling, insurance, damage risk)
    markup: 1.67          // 67% markup (cost $300 -> charge $500)
  },

  install: {
    none: 0,
    standard: 850,      // Twin/Twin, Twin/Full, Full/Full
    mid: 1200,          // Twin XL/Queen, Queen/Queen
    complex: 1800,      // Triple
    full: 2500          // Quad
  }
};
